// Opmaak en tabelstijl die de delen van het vlak Kosten & credits delen (W6 ronde 4).
//
// De regel die hier het zwaarst weegt (research §3.5 kosten): het scherm rekent
// geen bedrag uit. `usd` maakt alleen een getal op dat de server al stuurde —
// geen som, geen verschil, geen aandeel. De drie soorten bedrag (vastgelegd ·
// echt · berekend) staan elk in hun eigen kolom en gaan hier nooit bij elkaar
// op; waar een totaal staat, is het het totaal van de server.
//
// Bewust een echte <table> en niet het grid van de andere vlakken: deze
// tabellen hebben groepskoppen en een totaalrij over twee kolommen, en een
// schermlezer leest een tabel als tabel.

import type { CSSProperties, ReactNode } from "react";

import { Card } from "../../components/ui";
import { color, size } from "../../tokens";

/** "$74,66" — een bedrag in USD zoals de server het stuurt, op z'n Nederlands.
 *  Zelfde vorm als de zinnen van de server ("$0,03 van $100,00 deze maand").
 *
 *  Een bedrag boven nul maar onder een halve cent zou op twee decimalen
 *  "$0,00" worden — en dat leest als "niets gekost". Dus "< $0,01": het getal
 *  van de server blijft het getal, alleen de afronding liegt niet meer. Nul
 *  zelf blijft "$0,00". */
export function usd(n: number): string {
  if (n > 0 && n < 0.005) return "< $0,01";
  return `$${n.toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** "2026-09" → "september 2026". Onleesbaar = de sleutel, kaal maar eerlijk. */
export function maandNaam(maand: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(maand);
  if (!m) return maand;
  return new Date(Number(m[1]), Number(m[2]) - 1, 1).toLocaleDateString("nl-NL", {
    month: "long",
    year: "numeric",
  });
}

/** Kopcel: zelfde maat en kleur als `TableHead` (10px, hoofdletters, #fafafa). */
export function kopCel(rechts = false): CSSProperties {
  return {
    padding: "9px 14px",
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: color.faint,
    background: color.surfaceAlt,
    borderBottom: `1px solid ${color.lineSoft}`,
    textAlign: rechts ? "right" : "left",
    whiteSpace: "nowrap",
  };
}

export function cel(rechts = false): CSSProperties {
  return {
    padding: "10px 14px",
    fontSize: size.meta,
    color: color.body,
    borderTop: `1px solid ${color.lineFaint}`,
    textAlign: rechts ? "right" : "left",
    verticalAlign: "top",
    fontVariantNumeric: "tabular-nums",
  };
}

/** Kleine regel onder een waarde in een cel ("3 met bedrag"). */
export const klein: CSSProperties = { fontSize: 11, color: color.faint, marginTop: 2 };

/** Een tabel in een kaart die op een smal scherm binnen de kaart schuift, niet
 *  de pagina (zelfde afspraak als Gezondheid en Bronnen). */
export function Tabel({
  label,
  minBreedte,
  children,
}: {
  label: string;
  minBreedte: number;
  children: ReactNode;
}) {
  return (
    <Card style={{ padding: 0, overflow: "hidden" }}>
      <div style={{ overflowX: "auto" }}>
        <table
          aria-label={label}
          style={{ width: "100%", minWidth: minBreedte, borderCollapse: "collapse" }}
        >
          {children}
        </table>
      </div>
    </Card>
  );
}

/** Een bedrag van de server in een cel. Nul staat er gewoon (de server stuurde
 *  0, geen null), maar lichter, zodat het oog naar de echte bedragen gaat. */
export function Bedrag({ n }: { n: number }) {
  return <span style={{ color: n === 0 ? color.faint : color.ink }}>{usd(n)}</span>;
}
